import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {TYPES_TO_DISPLAY_NAMES} from 'analysis-tool/AnalysisToolConstants';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import {ButtonSize} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {capitalize, constantCaseToWords} from 'util/NameUtil';

import {DataFrameRetypeColumnOptions, RetypeColumnType} from './DataFrameHeaderMenuOptions';
import {DataFrameSharedPopover} from './DataFrameSharedPopover';

export interface DataFrameRetypeCreatorProps {
  canSelectColumn?: boolean;
  columns: Array<DataFrameColumnDef>;
  retypeColumnOptions: DataFrameRetypeColumnOptions;
  onCancel?: () => void;
  onSave?: () => void;
  initialSelectedColumnName: string;
}

interface InternalDataFrameRetypeCreatorProps {
  column: DataFrameColumnDef;
  onUpdateRetype: (type: string) => void;
  type: LeafType;
}

function InternalDataFrameRetypeCreator({
  column,
  type,
  onUpdateRetype,
}: InternalDataFrameRetypeCreatorProps): JSX.Element {
  const choices =
    PIPELINE_STEP_SHAPES[PipelineStepType.RETYPE_COLUMNS][0]?.config.retypeColumns.elementSpec.fields.type
      .choices;
  return (
    <div className={css(styles.retypeSection)}>
      <Chip
        className={css(styles.chip)}
        size={ChipSize.SMALL}
        icon={columnToTypeIcon(column)}
        text={column.displayName}
        isReadonly={true}
      />
      <SelectDropdown
        buttonSize={ButtonSize.SMALL}
        className={css(styles.selector)}
        displayName={capitalize(constantCaseToWords(type))}
        autoFocus={true}
        menu={
          <Menu height={MenuHeight.SMALL} width={MenuWidth.LARGE}>
            {choices.map((choice) => (
              <Option key={choice} value={TYPES_TO_DISPLAY_NAMES[choice]} onSelect={onUpdateRetype}>
                {TYPES_TO_DISPLAY_NAMES[choice]}
              </Option>
            ))}
          </Menu>
        }
      />
    </div>
  );
}

/**
 * Create a retype transformation.
 */
export function DataFrameRetypeCreator({
  canSelectColumn,
  columns,
  retypeColumnOptions,
  onCancel,
  onSave,
  initialSelectedColumnName,
}: DataFrameRetypeCreatorProps): JSX.Element {
  const [selectedColumnSystemName, setSelectedColumnSystemName] = useState<string>(initialSelectedColumnName);

  const defaultType = LeafType.TEXT;
  const [cachedType, setCachedType] = useState<LeafType>(defaultType);

  function onColumnSystemNameChange(columnSystemName: string): void {
    setSelectedColumnSystemName(columnSystemName);
    setCachedType(defaultType);
  }
  const column = columns.find((column) => column.systemName === selectedColumnSystemName)!;
  const isDisabled = column.type.leafType === cachedType;

  function handleUpdateRetype(type: LeafType): void {
    setCachedType(type);
  }

  function handleCancel(): void {
    setCachedType(defaultType);
    onCancel?.();
  }

  function handleSave(): void {
    retypeColumnOptions.retypeColumn(
      selectedColumnSystemName,
      Object.entries(TYPES_TO_DISPLAY_NAMES).find(([_, v]) => v === cachedType)?.[0]! as RetypeColumnType
    );
    onSave?.();
  }

  return (
    <DataFrameSharedPopover
      canSelectColumn={canSelectColumn}
      columns={columns}
      onCancel={handleCancel}
      onSave={handleSave}
      isDisabled={isDisabled}
      selectedColumnSystemName={selectedColumnSystemName}
      onColumnSystemNameChange={onColumnSystemNameChange}
      internalCreator={
        <InternalDataFrameRetypeCreator
          column={column}
          onUpdateRetype={handleUpdateRetype}
          type={cachedType}
        />
      }
    />
  );
}

export const styles = StyleSheet.create({
  chip: {
    minWidth: 125,
  },
  retypeSection: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: 10,
  },
  selector: {
    whiteSpace: 'nowrap',
  },
});
